import { UserNotificationsAPI } from "../APIs/user_notifications.api";
import {
  UserNotificationStatus,
  UserNotificationWithDetails,
} from "../types/user_notifications.types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";

export interface UserNotificationsType {
  notifications: UserNotificationWithDetails[] | null;
  isLoading: boolean;
  error: string | null;
  unreadCount: number;
  getUserNotifications: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
}

const UserNotificationsContext = createContext<
  UserNotificationsType | undefined
>(undefined);

export const UserNotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [notifications, setNotifications] = useState<
    UserNotificationWithDetails[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unreadCount = useMemo(() => {
    if (!notifications) return 0;
    return notifications.filter(
      (n) => n.status === UserNotificationStatus.UNREAD
    ).length;
  }, [notifications]);

  const getUserNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await UserNotificationsAPI.getUserNotifications();
      if (response.statusCode === 200) {
        const data = await response.data;
        if (Array.isArray(data)) {
          setNotifications(data);
        } else {
          setNotifications(null);
        }
      } else {
        setError("Error al obtener las notificaciones del usuario");
      }
    } catch (error) {
      setError("Error de conexión");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getUserNotifications();
  }, [getUserNotifications]);

  const markAsRead = useCallback(
    async (notificationId: number) => {
      try {
        const response = await UserNotificationsAPI.markAsRead(notificationId);
        if (response.statusCode === 200) {
          setNotifications(
            (prevNotifications) =>
              prevNotifications?.map((notification) =>
                notification.id === notificationId
                  ? { ...notification, status: UserNotificationStatus.READ }
                  : notification
              ) || null
          );
          await getUserNotifications();
        }
      } catch (error) {}
    },
    [getUserNotifications]
  );

  const deleteNotification = useCallback(
    async (notificationId: number) => {
      try {
        const response = await UserNotificationsAPI.deleteNotification(
          notificationId
        );
        if (response.statusCode === 200) {
          setNotifications(
            (prevNotifications) =>
              prevNotifications?.filter(
                (notification) => notification.id !== notificationId
              ) || null
          );
          await getUserNotifications();
        }
      } catch (error) {}
    },
    [getUserNotifications]
  );

  return (
    <UserNotificationsContext.Provider
      value={{
        notifications,
        isLoading,
        error,
        unreadCount,
        getUserNotifications,
        markAsRead,
        deleteNotification,
      }}
    >
      {children}
    </UserNotificationsContext.Provider>
  );
};

export const useUserNotifications = () => {
  const context = useContext(UserNotificationsContext);
  if (!context) {
    throw new Error(
      "useUserNotifications must be used within a UserNotificationsProvider"
    );
  }
  return context;
};
